import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { findPromptBySlug } from '../utils/urlUtils';
import { useProgress } from '../contexts/ProgressContext';
import { useAuth } from '../contexts/AuthContext';
import AnalyticsService from '../services/analyticsService';
import Header from './Header';
import Breadcrumb from './Breadcrumb';
import ActionButton from './ActionButton';
import { BodyMedium, BodyLarge, HeadlineMedium } from './Typography';
import { renderMarkdownToHTML } from '../utils/markdownRenderer';

const PromptDetail = ({ onNavigateToHome, onNavigateToCourses, onNavigateToProfile, onNavigateToWritersRoom, onLogOut, onBackToPrompts, onModeSwitch, showAdminMode = false }) => {
  const { promptSlug } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);

  const { 
    updateProgress,
    getLessonStatus
  } = useProgress();
  const { user, currentSession, userOrganizations } = useAuth();

  // Computed promptId for backward compatibility
  const promptId = prompt?.id;

  useEffect(() => {
    const fetchPromptBySlug = async () => {
      try {
        setLoading(true);
        
        // Fetch all prompts to find the one with matching slug
        const promptsRef = collection(db, 'prompts');
        const promptsSnapshot = await getDocs(promptsRef);
        const prompts = promptsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        const matchedPrompt = findPromptBySlug(prompts, promptSlug);
        
        if (matchedPrompt) {
          setPrompt(matchedPrompt);
          
          // Set prompt as active when loaded (if not already completed)
          if (getLessonStatus(matchedPrompt.id) === 'not_started') {
            updateProgress(matchedPrompt.id, { status: 'active' }, 'prompt');
          }
        } else {
          // Prompt not found, redirect to prompts
          setPrompt(null);
          navigate('/prompts');
        }
      } catch (error) {
        // EFFICIENT: Silent error handling, don't break user experience
        setPrompt(null);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    if (promptSlug) {
      fetchPromptBySlug();
    }
  }, [promptSlug, findPromptBySlug, getLessonStatus, updateProgress, navigate]);

  const getPromptStatus = () => {
    return getLessonStatus(promptId);
  };

  const handleCompletePrompt = async () => {
    // Mark prompt as completed
    updateProgress(promptId, { 
      status: 'completed',
      completed: true,
      completedAt: new Date().toISOString()
    }, 'prompt');
    
    // Track completion analytics
    if (user && currentSession && userOrganizations) {
      await AnalyticsService.trackCompletion(
        'prompt',
        promptId,
        currentSession,
        user.uid,
        userOrganizations,
        {
          promptTitle: prompt?.title,
          promptDescription: prompt?.description
        }
      );
    }
    
    // Navigate back to Prompts page after marking complete
    onBackToPrompts();
  };

  if (loading) {
    return (
      <div>
        <Header 
          title="Writing Prompt"
          onNavigateToCourses={onNavigateToCourses}
          onNavigateToProfile={onNavigateToProfile}
          onNavigateToWritersRoom={onNavigateToWritersRoom}
          onLogOut={onLogOut}
          onModeSwitch={onModeSwitch}
          showAdminMode={showAdminMode}
        />
        {/* role="status" announces loading message to screen readers */}
        <div role="status" aria-live="polite" className="flex justify-center items-center h-64">
          <div className="text-gray-600">Loading prompt...</div>
        </div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div>
        <Header 
          title="Writing Prompt"
          onNavigateToCourses={onNavigateToCourses}
          onNavigateToProfile={onNavigateToProfile}
          onNavigateToWritersRoom={onNavigateToWritersRoom}
          onLogOut={onLogOut}
          onModeSwitch={onModeSwitch}
          showAdminMode={showAdminMode}
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">Prompt not found</div>
        </div>
      </div>
    );
  }

  const promptStatus = getPromptStatus();

  return (
    <div>
      <Header 
        title="Writing Prompt"
        onNavigateToHome={onNavigateToHome}
        onNavigateToCourses={onNavigateToCourses}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToPrompts={onBackToPrompts}
        onNavigateToWritersRoom={onNavigateToWritersRoom}
        onLogOut={onLogOut}
        onModeSwitch={onModeSwitch}
      />
      
      {/* Main Content Area */}
      <div className="min-h-[calc(100vh-100px)] flex flex-col px-4 sm:px-8 pt-4 md:pt-6 pb-6 sm:pb-12">
        <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col">
          {/* Breadcrumbs */}
          <div className="mb-6">
            <Breadcrumb 
              items={[
                { 
                  label: 'Home', 
                  onClick: onNavigateToHome 
                },
                { 
                  label: 'Prompts', 
                  onClick: onBackToPrompts 
                },
                { 
                  label: prompt.title 
                }
              ]} 
            />
          </div>
          
          {/* Prompt Content Card - Centered */}
          <div className="flex-1 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 w-full">
            {/* Title — h1 equivalent for this page view */}
            <HeadlineMedium role="heading" aria-level={1} color="gray-800" className="mb-4 sm:mb-6 text-center sm:text-left">
              {prompt.title}
            </HeadlineMedium>
            
            {/* Body/Description */}
            {(prompt.body || prompt.description) && (
              <div className="mb-6 sm:mb-8">
                <BodyLarge 
                  color="gray-600" 
                  className="text-sm sm:text-base leading-relaxed"
                  dangerouslySetInnerHTML={renderMarkdownToHTML(prompt.body || prompt.description)}
                />
              </div>
            )}
            
            {/* Complete Button - Using ActionButton component for consistency */}
            <div className="flex justify-center sm:justify-end">
              <ActionButton
                onClick={handleCompletePrompt}
                disabled={promptStatus === 'completed'}
                showArrow={promptStatus !== 'completed'}
                fullWidth={false}
                size="md"
                variant={promptStatus === 'completed' ? 'completed' : 'primary'}
              >
                {promptStatus === 'completed' ? 'Completed!' : 'Complete'}
              </ActionButton>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptDetail;
